import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '../api';
import type { RoleFormData } from '../types';

const KEYS = {
  all: ['roles'] as const,
  list: (params?: Record<string, unknown>) => [...KEYS.all, 'list', params] as const,
  active: () => [...KEYS.all, 'active'] as const,
  availablePermissions: () => [...KEYS.all, 'available-permissions'] as const,
  myPermissions: () => [...KEYS.all, 'my-permissions'] as const,
  detail: (id: string) => [...KEYS.all, 'detail', id] as const,
};

export function useAvailablePermissions() {
  return useQuery({
    queryKey: KEYS.availablePermissions(),
    queryFn: async () => {
      const { data } = await adminApi.getAvailablePermissions();
      return { tab: data.tab, action: data.action, dataScope: data.dataScope };
    },
    staleTime: 5 * 60 * 1000, // 5 min — permission list rarely changes
  });
}

export function useActiveRoles() {
  return useQuery({
    queryKey: KEYS.active(),
    queryFn: async () => {
      const { data } = await adminApi.getActiveRoles();
      return data.roles;
    },
  });
}

export function useMyPermissions() {
  return useQuery({
    queryKey: KEYS.myPermissions(),
    queryFn: async () => {
      const { data } = await adminApi.getMyPermissions();
      return data;
    },
  });
}

export function useRoleDetail(id: string | undefined) {
  return useQuery({
    queryKey: KEYS.detail(id!),
    queryFn: async () => {
      const { data } = await adminApi.getRoleById(id!);
      return data.role;
    },
    enabled: !!id,
  });
}

export function useCreateRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: RoleFormData) => {
      const { data: result } = await adminApi.createRole(data);
      return result.role;
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: KEYS.all });
    },
  });
}

export function useUpdateRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<RoleFormData> }) => {
      const { data: result } = await adminApi.updateRole(id, data);
      return result.role;
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: KEYS.all });
    },
  });
}

export function useUpdateRolePermissions() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, permissions }: { id: string; permissions: string[] }) => {
      const { data: result } = await adminApi.updateRolePermissions(id, permissions);
      return result.role;
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: KEYS.all });
    },
  });
}

export function useDeleteRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await adminApi.deleteRole(id);
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: KEYS.all });
    },
  });
}

